import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Per-kind primary metric — what we plot on the sparkline and show as
// "latest". Hidden in the hook so callers (and tests) don't need to
// know which column maps to which kind.
function primaryValue(entry, kind) {
  if (kind === 'blood_pressure') return entry.systolic
  if (kind === 'heart_rate') return entry.bpm
  if (kind === 'glucose') return entry.glucose_mg_dl != null ? Number(entry.glucose_mg_dl) : null
  return null
}

// CRUD + summary stats for one vital kind. Filtered to the last `days`
// days so the chart doesn't grow unbounded. The DB defaults
// `measured_at` to now() — v1 always logs "now"; editing the timestamp
// is a follow-up.
export function useVitals({ kind, days = 90 } = {}) {
  const { user } = useAuth()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user || !kind) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const sinceIso = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
    const { data, error: err } = await supabase
      .from('vital_entries')
      .select('*')
      .eq('user_id', user.id)
      .eq('kind', kind)
      .gte('measured_at', sinceIso)
      .order('measured_at', { ascending: false })
    if (err) setError(err.message)
    else setEntries(data || [])
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, kind, days])

  useEffect(() => { fetch() }, [fetch])

  const latest = entries[0] || null

  const stats = useMemo(() => {
    const vals = entries
      .map((e) => primaryValue(e, kind))
      .filter((v) => typeof v === 'number' && Number.isFinite(v))
    if (vals.length === 0) return { min: null, max: null, avg: null, count: 0 }
    const sum = vals.reduce((s, v) => s + v, 0)
    return {
      min: Math.min(...vals),
      max: Math.max(...vals),
      avg: Number((sum / vals.length).toFixed(1)),
      count: vals.length,
    }
  }, [entries, kind])

  const create = async (payload) => {
    if (!user) return null
    const { data, error: err } = await supabase
      .from('vital_entries')
      .insert({ ...payload, user_id: user.id, kind })
      .select('*')
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    await fetch()
    return data
  }

  const remove = async (id) => {
    const { error: err } = await supabase.from('vital_entries').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetch()
    return true
  }

  return { entries, latest, stats, loading, error, create, remove, refetch: fetch }
}
